/**
 * usePushNotifications — React hook for Web Push subscription management.
 *
 * Usage:
 *   const { subscribeToPush } = usePushNotifications();
 *   // Call subscribeToPush() after a user action (e.g. "Start Design" click)
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../services/apiError';
import { alertAppDialog } from '../services/appDialogs';
import axiosInstance from '../services/axiosInstance';

const PUSH_SUBSCRIBED_KEY = 'push_notification_subscribed';
const PUSH_STATE_EVENT = 'push-subscription-state-changed';
let pendingSubscriptionOperation: Promise<unknown> = Promise.resolve();
let subscriptionIntentRevision = 0;

/** All mounted hook instances share one device: finish registration before disabling it. */
function serializeSubscription<T>(operation: () => Promise<T>): Promise<T> {
  const pending = pendingSubscriptionOperation.then(operation, operation);

  pendingSubscriptionOperation = pending.catch(() => {});

  return pending;
}

function publishSubscriptionState(active: boolean) {
  window.dispatchEvent(new window.CustomEvent(PUSH_STATE_EVENT, { detail: active }));
}

/** Convert a URL-safe base64 VAPID key to a Uint8Array. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);

  return Uint8Array.from(rawData, (c) => c.charCodeAt(0));
}

/** Fetch VAPID public key from the backend. */
async function fetchVapidKey(): Promise<string | null> {
  try {
    const resp = await axiosInstance.get('/api/v1/push/vapid-key');

    return resp.data?.public_key || null;
  } catch {
    return null;
  }
}

async function getBrowserSubscription(reg: Awaited<ReturnType<typeof navigator.serviceWorker.register>>) {
  const existing = await reg.pushManager.getSubscription();

  if (existing) return existing;
  const vapidKey = await fetchVapidKey();

  if (!vapidKey) return null;

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });
}

/** Register service worker, subscribe via PushManager, and send to backend. */
async function registerSubscribeAndSend(): Promise<boolean> {
  const reg = await navigator.serviceWorker.register('/sw.js');

  await navigator.serviceWorker.ready;
  const sub = await getBrowserSubscription(reg);

  if (!sub) return false;

  await axiosInstance.post('/api/v1/push/subscribe', { subscription: sub.toJSON() });

  return true;
}

/** Read an already issued token without asking native code to prompt for permission. */
function existingExpoToken(ignorePermissionError = false): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nativeWindow = window as any;

  if (!ignorePermissionError && nativeWindow.__EXPO_PUSH_ERROR__) return null;

  return nativeWindow.__EXPO_PUSH_TOKEN__ || localStorage.getItem('__EXPO_PUSH_TOKEN__');
}

function expoSubscription(token: string) {
  return { endpoint: token, is_expo_token: true, token };
}

async function registerWithoutPrompt(isRNWebView: boolean): Promise<boolean> {
  if (!isRNWebView) {
    // eslint-disable-next-line no-undef
    return Notification.permission === 'granted' && await registerSubscribeAndSend();
  }
  const token = existingExpoToken();

  if (!token) return false;
  await axiosInstance.post('/api/v1/push/subscribe', { subscription: expoSubscription(token) });

  return true;
}

async function restorePreviousSubscription(allowed: boolean, isRNWebView: boolean): Promise<boolean> {
  if (!allowed || localStorage.getItem(PUSH_SUBSCRIBED_KEY) !== 'true') return false;
  try {
    return await registerWithoutPrompt(isRNWebView);
  } catch {
    // Preserve enabled intent so a later focus/login can retry without a prompt.
    return false;
  }
}

function useSharedSubscriptionState() {
  const state = useState(false);
  const [, setIsSubscribed] = state;

  useEffect(() => {
    const update = (event: Event) => setIsSubscribed((event as Event & { detail: boolean }).detail);

    window.addEventListener(PUSH_STATE_EVENT, update);

    return () => window.removeEventListener(PUSH_STATE_EVENT, update);
  }, [setIsSubscribed]);

  return state;
}

/** Unsubscribe from PushManager and deactivate on the backend. */
async function clearBrowserSubscription(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();

  if (sub) {
    try {
      await axiosInstance.delete('/api/v1/push/subscribe', { data: { subscription: sub.toJSON() } });
    } finally {
      // A backend outage must not keep this browser receiving notifications.
      await sub.unsubscribe();
    }
  }
}

async function registerExplicitSubscription(isRNWebView: boolean, intentRevision: number): Promise<void> {
  if (intentRevision !== subscriptionIntentRevision) return;

  if (isRNWebView) {
    try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let expoToken = (window as any).__EXPO_PUSH_TOKEN__;

      // If not immediately available, ask RN for it and wait a bit
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rnWebView = (window as any).ReactNativeWebView;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let expoError = (window as any).__EXPO_PUSH_ERROR__;

      if (!expoToken && rnWebView) {
        rnWebView.postMessage(JSON.stringify({ type: 'GET_EXPO_PUSH_TOKEN' }));

        // Poll for up to 5 seconds waiting for RN to natively generate the token
        for (let i = 0; i < 20; i++) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          expoToken = (window as any).__EXPO_PUSH_TOKEN__;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          expoError = (window as any).__EXPO_PUSH_ERROR__;
          if (expoToken || expoError) break;
        }
      }

      const localToken = localStorage.getItem('__EXPO_PUSH_TOKEN__');

      if (!expoToken) expoToken = localToken;

      if (expoError) {
        console.warn('[PUSH] Subscription failed natively:', expoError);
        void alertAppDialog({ title: 'Could not enable notifications',
          message: 'We couldn’t enable notifications. Check notification permissions and try again.' });

        return;
      }

      if (!expoToken) {
        console.warn('[PUSH] Subscription requested but __EXPO_PUSH_TOKEN__ is not yet available');
        void alertAppDialog({ title: 'Notifications are not ready',
          message: 'Allow notification permission, restart the app, and try again.' });

        return;
      }

      await axiosInstance.post('/api/v1/push/subscribe', { subscription: expoSubscription(expoToken) });
      if (intentRevision === subscriptionIntentRevision) {
        localStorage.setItem(PUSH_SUBSCRIBED_KEY, 'true');
        publishSubscriptionState(true);
      }
    } catch (err: unknown) {
      console.error('[PUSH] Failed to subscribe Expo token:', err);
      if (typeof window !== 'undefined') {
        void alertAppDialog({ title: 'Could not enable notifications',
          message: errorMessage(err, 'We couldn’t enable notifications. Check notification permissions and try again.') });
      }
    }

    return;
  }

  try {
    if (!(await registerSubscribeAndSend())) return;
    if (intentRevision === subscriptionIntentRevision) {
      localStorage.setItem(PUSH_SUBSCRIBED_KEY, 'true');
      publishSubscriptionState(true);
    }
  } catch {
  // Push is best-effort — fail silently
  }
}

interface UsePushNotificationsReturn {
  subscribeToPush: () => Promise<void>;
  unsubscribePush: () => Promise<void>;
  isSubscribed: boolean;
  isSupported: boolean;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user, isAuthenticated } = useAuth();
  const isSupported =
    (typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window) ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (typeof window !== 'undefined' && !!(window as any).__IS_RN_WEBVIEW__);

  // A cached preference is intent, not confirmation that this user/device is registered.
  const [isSubscribed, setIsSubscribed] = useSharedSubscriptionState();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isRNWebView = typeof window !== 'undefined' ? !!(window as any).__IS_RN_WEBVIEW__ : false;

    let alive = true;
    const restore = () => serializeSubscription(async () => {
      if (!alive) return;
      const registered = await restorePreviousSubscription(isSupported && isAuthenticated, isRNWebView);

      if (alive) setIsSubscribed(registered && localStorage.getItem(PUSH_SUBSCRIBED_KEY) === 'true');
    });

    void restore();
    window.addEventListener('focus', restore);

    return () => { alive = false; window.removeEventListener('focus', restore); };
  }, [isSupported, isAuthenticated, user?.id, setIsSubscribed]);

  const subscribeToPush = useCallback(async () => {
    if (!isSupported || !isAuthenticated) return;
    const intentRevision = ++subscriptionIntentRevision;
    // Request browser permission while this explicit action still has user activation.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isRNWebView = (window as any).__IS_RN_WEBVIEW__;

    if (!isRNWebView) {
      // eslint-disable-next-line no-undef
      if (Notification.permission === 'denied') return;
      // eslint-disable-next-line no-undef
      if (Notification.permission !== 'granted' && await Notification.requestPermission() !== 'granted') return;
    }

    await serializeSubscription(() => registerExplicitSubscription(isRNWebView, intentRevision));
  }, [isSupported, isAuthenticated]);

  const unsubscribePush = useCallback(async () => {
    if (!isSupported || !isAuthenticated) return;
    subscriptionIntentRevision += 1;
    // Record the opt-out before awaiting an earlier registration or network request.
    localStorage.setItem(PUSH_SUBSCRIBED_KEY, 'false');
    publishSubscriptionState(false);
    await serializeSubscription(async () => {
      try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isRNWebView = (window as any).__IS_RN_WEBVIEW__;

        if (!isRNWebView) {
          await clearBrowserSubscription();
        } else {
          const token = existingExpoToken(true);

          if (!token) return;
          await axiosInstance.delete('/api/v1/push/subscribe', { data: { subscription: expoSubscription(token) } });
        }

      } catch { /* best-effort */ }
    });
  }, [isSupported, isAuthenticated]);

  return { subscribeToPush, unsubscribePush, isSubscribed, isSupported };
}
